import React, { useState, useEffect, useRef } from "react";
import Overridable from "react-overridable";
import {
  Container,
  Header,
  Grid,
  Button,
  Tab,
  Message,
  Icon,
  Segment,
  Label,
  Dropdown,
} from "semantic-ui-react";
import { searchHistoryApi } from "@api/search_history/search_history";

import Overview from "./OpacOverview";
import SearchAnalysis from "./OpacSearchAnalysis";
import Reports from "./OpacReports";
import OPACUsage from "./OpacUsage";

const DEFAULT_DATE_RANGE = "30";
const DEFAULT_FORMAT = "csv";
const DEFAULT_DATA_POINTS = ["searches", "frequent_items"];
const DEFAULT_SESSION_DURATION = 3;
const MAX_SESSION_DURATION = 30;
const MAX_RECORDS = 2000;
const RECORDS_PER_PAGE = 500;
const TOP_TERMS_LIMIT = 20;
const PEAK_HOURS_LIMIT = 6;
const TREND_DAYS_LIMIT = 14;

// Date range options for the dropdown
// Update DATE_RANGE_OPTIONS constant (around line 28)
const DATE_RANGE_OPTIONS = [
  {
    key: "1",
    text: "Today",
    value: "1",
    icon: "calendar alternate outline",
  },
  {
    key: "7",
    text: "7 days",
    value: "7",
    icon: "calendar alternate outline",
  },
  {
    key: "30",
    text: "30 days",
    value: "30",
    icon: "calendar alternate outline",
  },
  {
    key: "90",
    text: "90 days",
    value: "90",
    icon: "calendar alternate outline",
  },
  {
    key: "180",
    text: "6 months",
    value: "180",
    icon: "calendar alternate outline",
  },
  {
    key: "365",
    text: "Last year",
    value: "365",
    icon: "calendar alternate outline",
  },
];

const initialOverviewStats = {
  totalSearches: 0,
  successfulSearches: 0,
  failedSearches: 0,
  successRate: 0,
  avgResponseTime: 0,
};

const initialUsageStats = {
  activeUsers: 0,
  totalSessions: 0,
  avgSessionDuration: 0,
  peakUsageHours: [],
  topCategories: [],
  userEngagement: {
    newUsers: 0,
    returningUsers: 0,
    bounceRate: 0,
  },
  systemPerformance: {
    avgLoadTime: 0,
    uptime: 0,
    errorRate: 0,
  },
};

const OPACStatisticsDashboard = (props) => {
  const [selectedDateRange, setSelectedDateRange] =
    useState(DEFAULT_DATE_RANGE);
  const [selectedFormat, setSelectedFormat] = useState(DEFAULT_FORMAT);
  const [selectedDataPoints, setSelectedDataPoints] =
    useState(DEFAULT_DATA_POINTS);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overviewStats, setOverviewStats] = useState(initialOverviewStats);
  const [searchTrends, setSearchTrends] = useState([]);
  const [frequentTerms, setFrequentTerms] = useState([]);
  const [frequentItems] = useState([]);
  const [usageStats, setUsageStats] = useState(initialUsageStats);
  const [customDateRange, setCustomDateRange] = useState({
    fromDate: "",
    toDate: "",
  });

  // Add ref to track component mount status
  const isMountedRef = useRef(true);

  // Add ref to track ongoing requests for cleanup
  const requestsRef = useRef(new Set());

  useEffect(() => {
    // Set mounted to true when component mounts
    isMountedRef.current = true;

    const testAndLoad = async () => {
      if (!isMountedRef.current) return;

      setStatsLoading(true);
      try {
        await searchHistoryApi.testConnection();
        if (!isMountedRef.current) return;

        await loadSystemWideStatistics();
      } catch (testError) {
        if (!isMountedRef.current) return;

        setError(
          "Cannot connect to the statistics service. Please check your connection and try again."
        );
      } finally {
        if (isMountedRef.current) {
          setStatsLoading(false);
        }
      }
    };

    testAndLoad();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      // Cancel any ongoing requests
      requestsRef.current.forEach((request) => {
        if (request && request.cancel) {
          request.cancel();
        }
      });
      requestsRef.current.clear();
    };
  }, [selectedDateRange, customDateRange]);

  const getAllSystemSearches = async (days) => {
    if (!isMountedRef.current) return { data: { total: 0, hits: [] } };

    const allSystemSearches = await searchHistoryApi.listAllUsers({
      days,
      per_page: RECORDS_PER_PAGE,
      sort_by: "date",
      sort_order: "desc",
    });

    if (!isMountedRef.current) return { data: { total: 0, hits: [] } };

    let allHits = allSystemSearches?.data?.hits || [];
    const totalAvailable = allSystemSearches?.data?.total || 0;

    if (totalAvailable > RECORDS_PER_PAGE) {
      const maxRecords = Math.min(totalAvailable, MAX_RECORDS);
      const additionalPages = Math.ceil(
        (maxRecords - RECORDS_PER_PAGE) / RECORDS_PER_PAGE
      );

      for (
        let page = 2;
        page <= additionalPages + 1 && allHits.length < maxRecords;
        page++
      ) {
        if (!isMountedRef.current) break;

        const pageResponse = await searchHistoryApi.listAllUsers({
          days,
          per_page: RECORDS_PER_PAGE,
          page: page,
          sort_by: "date",
          sort_order: "desc",
        });

        if (!isMountedRef.current) break;

        if (pageResponse?.data?.hits) {
          allHits = [...allHits, ...pageResponse.data.hits];
        }
      }
    }

    return {
      ...allSystemSearches,
      data: {
        ...allSystemSearches.data,
        hits: allHits,
        total: totalAvailable,
      },
    };
  };

  const loadSystemWideStatistics = async () => {
    if (!isMountedRef.current) return;

    setError(null);

    const days =
      selectedDateRange === "custom" ? 365 : parseInt(selectedDateRange);

    let allSystemSearches = { data: { total: 0, hits: [] } };

    try {
      allSystemSearches = await getAllSystemSearches(days);
      if (!isMountedRef.current) return;
    } catch (listError) {
      // Silent fail for search list
      if (!isMountedRef.current) return;
    }

    const hits = allSystemSearches?.data?.hits || [];
    const totalSearches = allSystemSearches?.data?.total || 0;

    const successfulHits = hits.filter((hit) => hit.success);
    const failedHits = hits.filter((hit) => !hit.success);
    const successfulCount = successfulHits.length;
    const failedCount = failedHits.length;
    const successRate =
      totalSearches > 0
        ? Math.round((successfulCount / totalSearches) * 100)
        : 0;

    const avgResponseTime =
      hits.length > 0
        ? Math.round(
            hits
              .filter((hit) => hit.response_time_ms)
              .reduce((sum, hit) => sum + hit.response_time_ms, 0) /
              Math.max(1, hits.filter((hit) => hit.response_time_ms).length)
          )
        : 0;

    // Only update state if component is still mounted
    if (!isMountedRef.current) return;

    setOverviewStats({
      totalSearches,
      successfulSearches: successfulCount,
      failedSearches: failedCount,
      successRate,
      avgResponseTime,
    });

    setSearchTrends(processSystemSearchTrends(hits));
    setFrequentTerms(processSystemFrequentTerms(hits));
    setUsageStats(generateSystemUsageStats(hits, days));
  };

  const processSystemSearchTrends = (searches) => {
    const trends = {};
    const now = new Date();
    const daysBack = parseInt(selectedDateRange) || 30;

    for (let i = daysBack - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split("T")[0];
      trends[dateKey] = {
        date: dateKey,
        total: 0,
        successful: 0,
        failed: 0,
        uniqueUsers: new Set(),
      };
    }

    searches.forEach((search) => {
      const searchDate = new Date(search.created_at)
        .toISOString()
        .split("T")[0];
      if (trends[searchDate]) {
        trends[searchDate].total++;
        trends[searchDate].uniqueUsers.add(search.user_id);
        if (search.success) {
          trends[searchDate].successful++;
        } else {
          trends[searchDate].failed++;
        }
      }
    });

    Object.values(trends).forEach((trend) => {
      trend.uniqueUsers = trend.uniqueUsers.size;
    });

    return Object.values(trends).slice(-TREND_DAYS_LIMIT);
  };

  const processSystemFrequentTerms = (searches) => {
    const termCounts = {};
    const termUserCounts = {};

    searches.forEach((search) => {
      const term = search.search_query?.toLowerCase().trim();
      if (term && term.length > 0) {
        termCounts[term] = (termCounts[term] || 0) + 1;

        if (!termUserCounts[term]) {
          termUserCounts[term] = new Set();
        }
        termUserCounts[term].add(search.user_id);
      }
    });

    return Object.entries(termCounts)
      .map(([term, count]) => ({
        term,
        count,
        uniqueUsers: termUserCounts[term].size,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, TOP_TERMS_LIMIT);
  };

  const generateSystemUsageStats = (searches, days) => {
    const uniqueUsers = new Set(searches.map((s) => s.user_id).filter(Boolean))
      .size;
    const totalSearches = searches.length;

    const userSessions = {};
    searches.forEach((search) => {
      const userId = search.user_id;
      if (!userId) return;

      const date = new Date(search.created_at).toDateString();
      const sessionKey = `${userId}_${date}`;

      if (!userSessions[sessionKey]) {
        userSessions[sessionKey] = [];
      }
      userSessions[sessionKey].push(search);
    });

    const totalSessions = Object.keys(userSessions).length;

    const sessionDurations = Object.values(userSessions).map(
      (sessionSearches) => {
        if (sessionSearches.length === 1) {
          return DEFAULT_SESSION_DURATION;
        }

        const sortedSearches = sessionSearches.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );

        const firstSearch = new Date(sortedSearches[0].created_at);
        const lastSearch = new Date(
          sortedSearches[sortedSearches.length - 1].created_at
        );
        const durationMinutes = (lastSearch - firstSearch) / (1000 * 60);

        return Math.max(
          1,
          Math.min(
            durationMinutes || DEFAULT_SESSION_DURATION,
            MAX_SESSION_DURATION
          )
        );
      }
    );

    const avgSessionDuration =
      sessionDurations.length > 0
        ? Math.round(
            sessionDurations.reduce((a, b) => a + b, 0) /
              sessionDurations.length
          )
        : 5;

    const hourlyUsage = {};
    const hourlyUsers = {};

    searches.forEach((search) => {
      if (!search.user_id) return;

      const hour = new Date(search.created_at).getHours();
      hourlyUsage[hour] = (hourlyUsage[hour] || 0) + 1;

      if (!hourlyUsers[hour]) {
        hourlyUsers[hour] = new Set();
      }
      hourlyUsers[hour].add(search.user_id);
    });

    const peakUsageHours = Object.entries(hourlyUsage)
      .sort(([, a], [, b]) => b - a)
      .slice(0, PEAK_HOURS_LIMIT)
      .map(([hour, count]) => ({
        hour: `${hour.padStart(2, "0")}:00`,
        searches: count,
        uniqueUsers: hourlyUsers[hour].size,
        percentage: Math.round((count / totalSearches) * 100),
      }));

    const userFirstSearches = {};
    const userSearchCounts = {};

    searches.forEach((search) => {
      if (!search.user_id) return;

      const userId = search.user_id;
      const searchDate = new Date(search.created_at);

      if (
        !userFirstSearches[userId] ||
        searchDate < userFirstSearches[userId]
      ) {
        userFirstSearches[userId] = searchDate;
      }

      userSearchCounts[userId] = (userSearchCounts[userId] || 0) + 1;
    });

    const periodStart = new Date();
    periodStart.setDate(periodStart.getDate() - days);

    let newUsers = 0;
    let returningUsers = 0;

    Object.entries(userFirstSearches).forEach(([userId, firstSearch]) => {
      if (firstSearch >= periodStart) {
        newUsers++;
      } else {
        returningUsers++;
      }
    });

    const singleSearchUsers = Object.values(userSearchCounts).filter(
      (count) => count === 1
    ).length;
    const bounceRate =
      uniqueUsers > 0 ? Math.round((singleSearchUsers / uniqueUsers) * 100) : 0;

    const responseTimes = searches
      .filter(
        (search) => search.response_time_ms && search.response_time_ms > 0
      )
      .map((search) => search.response_time_ms);

    const avgLoadTime =
      responseTimes.length > 0
        ? Math.round(
            responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
          )
        : 0;

    const failedSearches = searches.filter(
      (s) => s.success === false || s.success === 0
    );

    const errorRate =
      totalSearches > 0
        ? Math.round((failedSearches.length / totalSearches) * 100)
        : 0;

    const uptime =
      totalSearches > 0
        ? Math.round(
            ((totalSearches - failedSearches.length) / totalSearches) * 100
          )
        : 99.5;

    return {
      activeUsers: uniqueUsers,
      totalSessions,
      avgSessionDuration,
      peakUsageHours,
      userEngagement: {
        newUsers,
        returningUsers,
        bounceRate,
      },
      systemPerformance: {
        avgLoadTime,
        uptime,
        errorRate,
      },
    };
  };

  const handleDateRangeChange = (event, { value }) => {
    setSelectedDateRange(value);
  };

  const handleExport = async () => {
    if (!isMountedRef.current) return;

    setLoading(true);

    const exportData = {
      dateRange: selectedDateRange,
      generatedAt: new Date().toISOString(),
      systemWide: true,
      totalSystemUsers: usageStats.activeUsers,
      overviewStats: selectedDataPoints.includes("searches")
        ? overviewStats
        : null,
      frequentTerms: selectedDataPoints.includes("frequent_terms")
        ? frequentTerms
        : null,
      frequentItems: selectedDataPoints.includes("frequent_items")
        ? frequentItems
        : null,
      searchTrends: selectedDataPoints.includes("search_patterns")
        ? searchTrends
        : null,
      usageStats: selectedDataPoints.includes("usage_analytics")
        ? usageStats
        : null,
      userEngagement: selectedDataPoints.includes("user_engagement")
        ? usageStats.userEngagement
        : null,
      systemPerformance: selectedDataPoints.includes("system_performance")
        ? usageStats.systemPerformance
        : null,
    };

    await new Promise((resolve) => setTimeout(resolve, 2000));

    if (!isMountedRef.current) return;

    const dataStr =
      selectedFormat === "json"
        ? JSON.stringify(exportData, null, 2)
        : convertToCSV(exportData);

    const dataBlob = new Blob([dataStr], {
      type: selectedFormat === "json" ? "application/json" : "text/csv",
    });

    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `system-opac-statistics-${
      new Date().toISOString().split("T")[0]
    }.${selectedFormat === "json" ? "json" : "csv"}`;
    link.click();
    URL.revokeObjectURL(url);

    if (isMountedRef.current) {
      setLoading(false);
    }
  };

  const convertToCSV = (data) => {
    let csv = "OPAC Usage Statistics Report\n\n";

    if (data.systemWide) {
      csv += `Report Type: Analysis\n`;
      csv += `Total OPAC Users: ${data.totalSystemUsers}\n`;
      csv += `Generated: ${new Date(data.generatedAt).toLocaleString()}\n`;
      csv += `Date Range: Last ${data.dateRange} days\n\n`;
    }

    if (data.overviewStats) {
      csv += "OPAC Overview Statistics\n";
      csv += "Metric,Value\n";
      csv += `Total Searches,${data.overviewStats.totalSearches}\n\n`;
    }

    if (data.frequentTerms?.length > 0) {
      csv += "Most Popular Search Terms\n";
      csv += "Term,Total Searches\n";
      data.frequentTerms.forEach((item) => {
        csv += `"${item.term}",${item.count}\n`;
      });
      csv += "\n";
    }

    if (data.usageStats) {
      csv += "System Usage Analytics\n";
      csv += "Metric,Value\n";
      csv += `Total Active Users,${data.usageStats.activeUsers}\n`;
      csv += `Total Sessions,${data.usageStats.totalSessions}\n`;
      csv += `Average Session Duration,${Math.round(
        data.usageStats.avgSessionDuration / 60
      )} minutes\n`;
      csv += `System Bounce Rate,${data.usageStats.userEngagement.bounceRate}%\n\n`;

      if (data.usageStats.peakUsageHours?.length > 0) {
        csv += "Peak Usage Hours\n";
        csv += "Time,Total Searches,Percentage\n";
        data.usageStats.peakUsageHours.forEach((hour) => {
          csv += `${hour.hour},${hour.searches},${hour.percentage}%\n`;
        });
        csv += "\n";
      }
    }

    if (data.systemPerformance) {
      csv += "System Performance\n";
      csv += "Metric,Value\n";
      csv += `Average Load Time,${data.systemPerformance.avgLoadTime}ms\n`;
      csv += `System Uptime,${data.systemPerformance.uptime}%\n`;
      csv += `Error Rate,${data.systemPerformance.errorRate}%\n\n`;
    }

    return csv;
  };

  // Add retry function that checks mount status
  const handleRetry = () => {
    if (!isMountedRef.current) return;
    loadSystemWideStatistics();
  };

  const OverviewTab = () => (
    <Tab.Pane>
      <Overview
        overviewStats={overviewStats}
        searchTrends={searchTrends}
        statsLoading={statsLoading}
      />
    </Tab.Pane>
  );

  const SearchAnalysisTab = () => (
    <Tab.Pane>
      <SearchAnalysis
        frequentTerms={frequentTerms}
        frequentItems={frequentItems}
        searchTrends={searchTrends}
        statsLoading={statsLoading}
      />
    </Tab.Pane>
  );

  const UsageReportTab = () => (
    <Tab.Pane>
      <OPACUsage usageStats={usageStats} statsLoading={statsLoading} />
    </Tab.Pane>
  );

  const ReportsTab = () => (
    <Tab.Pane>
      <Reports
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        selectedFormat={selectedFormat}
        setSelectedFormat={setSelectedFormat}
        selectedDataPoints={selectedDataPoints}
        setSelectedDataPoints={setSelectedDataPoints}
        customDateRange={customDateRange}
        setCustomDateRange={setCustomDateRange}
        handleExport={handleExport}
        loading={loading}
        overviewStats={overviewStats}
        searchTrends={searchTrends}
        frequentTerms={frequentTerms}
        usageStats={usageStats}
      />
    </Tab.Pane>
  );

  const tabPanes = [
    {
      menuItem: {
        key: "overview",
        icon: "dashboard",
        content: "Opac Overview",
        color: "green",
      },
      render: OverviewTab,
    },
    {
      menuItem: {
        key: "search-analysis",
        icon: "search",
        content: "Search Analysis",
        color: "green",
      },
      render: SearchAnalysisTab,
    },
    {
      menuItem: {
        key: "usage-report",
        icon: "chart bar",
        content: "Opac Usage",
        color: "green",
      },
      render: UsageReportTab,
    },
    {
      menuItem: {
        key: "reports",
        icon: "file alternate",
        content: "Reports",
        color: "green",
      },
      render: ReportsTab,
    },
  ];

  if (error) {
    return (
      <Container>
        <Message negative>
          <Message.Header>Error Loading System Statistics</Message.Header>
          <p>{error}</p>
          <Button onClick={handleRetry}>
            <Icon name="refresh" />
            Retry
          </Button>
        </Message>
      </Container>
    );
  }

  return (
    <Overridable id="OPACStatisticsDashboard.render">
      <Container fluid className="opac-statistics-dashboard">
        <Header as="h1" className="dashboard-header">
          OPAC Usage Statistics Dashboard
        </Header>

        <Segment basic>
          <Grid>
            <Grid.Column width={10}>
              <Label
                as="div"
                color="green"
                size="small"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "0.4em 0.8em",
                  fontSize: "0.85em",
                }}
              >
                <Icon
                  name="calendar alternate outline"
                  size="small"
                  style={{ marginRight: "0.4em" }}
                />
                <span style={{ marginRight: "0.4em" }}>Date Range:</span>
                <Dropdown
                  inline
                  options={DATE_RANGE_OPTIONS}
                  value={selectedDateRange}
                  onChange={handleDateRangeChange}
                  style={{
                    color: "inherit",
                    fontWeight: "inherit",
                    background: "transparent",
                    border: "none",
                    minHeight: "auto",
                    padding: 0,
                    fontSize: "inherit",
                  }}
                />
              </Label>
            </Grid.Column>
          </Grid>
        </Segment>

        <Tab panes={tabPanes} />
      </Container>
    </Overridable>
  );
};

OPACStatisticsDashboard.displayName = "OPACStatisticsDashboard";

export default Overridable.component(
  "OPACStatisticsDashboard",
  OPACStatisticsDashboard
);
